import React, { useContext, useState } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import ScriptBox from "../../../components/ScriptBox/ScriptBox";
import Textarea from "../../../components/Textarea/Textarea";
import { ReactComponent as SendIcon } from "../../../assets/icons/send-icon.svg";
import { ReactComponent as PersonIcon } from "../../../assets/icons/person.svg";
import SimpleButton from "../../../components/SimpleButton/SimpleButton";
import { getService, postService } from "../../../services/axios/services";
import { toastSuccess } from "../../../utils";
import AppContext from "../../../context/appContext";
import "./styles/FileShareBox.scss";
import AsyncLoading from "../../../components/AsyncLoading/AsyncLoading";
import CheckBox from "../../../components/CheckBox/CheckBox";
import DeleteIcon from "../../../components/VectorIcons/DeleteIcon";

const SharedBox = ({
  open,
  setOpen,
  selectedFiles = [],
  onClose,
  reload,
  projectId,
}) => {
  let initialState = { loading: true, data: [], hasOptions: false };

  const [stateUsers, setUsers] = useState(initialState);
  const [activeTab, setActiveTab] = useState("compartilhar");
  const [emails, setEmails] = useState([]);
  const [message, setMessage] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const appContext = useContext(AppContext);

  const searchUsers = (inputValue, callback) => {
    if (inputValue && inputValue.length > 0) {
      getService({ endpoint: "users/ad", params: { name: inputValue } }).then(
        (users) => {
          const _users =
            users.data
              ?.filter((user) => user.mail)
              .map((user) => ({
                label: user.mail,
                value: user.mail,
                displayName: user.displayName,
                jobTitle: user.jobTitle || "—",
                email: user.mail,
              })) || [];

          setUsers({
            ...stateUsers,
            data: _users,
          });
          callback(_users);
        }
      );
    }
  };

  const handleChangeEmails = (newValue) => {
    setEmails(newValue || []);
  };

  const handleChangeMessage = (event) => {
    setMessage(event.target.value);
  };

  const handleShare = () => {
    const emailList = emails.map((e) => e.value);
    const attachmentIds = selectedFiles.map(
      (file) => file?.attachment?.id ?? file.id
    );

    const endpoint = "sharing/attachments/permissions/share";
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

    const postObj = {
      attachmentIds,
      emails: emailList,
      message,
      expireAt: new Date(Date.now() + THIRTY_DAYS).toISOString(),
    };

    appContext.setShowGlassLoading(true);

    postService({ endpoint: endpoint, postData: postObj })
      .then(() => {
        appContext.setShowGlassLoading(false);
        reload();
        handleClose();
        toastSuccess("Arquivos compartilhados com sucesso!");
      })
      .catch((e) => {
        console.error("Erro ao compartilhar:", e);
        appContext.setShowGlassLoading(false);
      });
  };

  const handleClose = () => {
    if (typeof onClose === "function") onClose();
    else if (typeof setOpen === "function") setOpen(false);
  };

  const getTitle = () => {
    if (selectedFiles.length === 1) {
      const file = selectedFiles[0];
      const fileName = file.fileName || file.name || "arquivo";
      return `Gerenciar "${fileName}"`;
    }
    if (selectedFiles.length > 1) {
      return `Compartilhar "${selectedFiles.length} arquivos"`;
    }
    return "Compartilhar arquivos";
  };

  const toggleSelectOne = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPermissions.length === permissions.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(permissions.map((p) => p.id));
    }
  };

  const handleRevoke = () => {
    if (selectedPermissions.length === 0) return;

    appContext.setShowGlassLoading(true);

    postService({
      endpoint: "sharing/attachments/permissions/revoke",
      postData: {
        shareIds: selectedPermissions,
      },
    })
      .then(() => {
        toastSuccess("Permissão de acesso removida com sucesso!");
        setSelectedPermissions([]);
        fetchPermissions();
      })
      .catch((e) => {
        console.error("Erro ao revogar:", e);
      })
      .finally(() => {
        appContext.setShowGlassLoading(false);
        reload();
      });
  };

  const formatName = (fullName = "") => {
    if (!fullName) return "";

    const parts = fullName.trim().split(" ").filter(Boolean);

    if (parts.length <= 2) return fullName;

    const first = parts[0];
    const last = parts[parts.length - 1];

    return `${first} ${last}`;
  };

  const isPermissionValid = (item) => {
    const now = new Date();

    if (item.revokedAt) return false;

    if (!item.expiredAt) return true;

    const expiredDate = new Date(item.expiredAt);
    return expiredDate >= now;
  };

  const fetchPermissions = () => {
    if (!projectId || !selectedFiles.length) return;

    const attachmentId = selectedFiles[0]?.attachmentId ?? selectedFiles[0]?.id;

    setLoadingPermissions(true);

    getService({
      endpoint: `sharing/attachments/projects/${projectId}/permissions`,
    })
      .then((res) => {
        const all = res || [];

        const filtered = all
          .filter((item) => item.attachmentId === attachmentId)
          .filter(isPermissionValid);

        setPermissions(filtered);
      })
      .catch((err) => {
        console.error("Erro ao buscar permissões:", err);
        setPermissions([]);
      })
      .finally(() => {
        setLoadingPermissions(false);
        setPermissionsLoaded(true);
      });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === "gerenciar" && !permissionsLoaded) {
      fetchPermissions();
    }
  };

  const renderPermissions = () => {
    if (loadingPermissions) {
      return (
        <div className="ViewWrapper">
          <div className="loadingPermissionsBox">
            <div className="loadingCircle" />
          </div>
        </div>
      );
    }

    if (!permissions.length) {
      return (
        <div className="ViewWrapper">
          <div className="emptyPermissions">
            Esse arquivo ainda não foi compartilhado.
          </div>
        </div>
      );
    }

    return (
      <div className="ViewWrapper">
        <div className="permissionsTable noTable">
          <div className="permissionsRow header">
            <span className="cell checkbox">
              <CheckBox
                isActive={selectedPermissions.length === permissions.length}
                event={toggleSelectAll}
                label=""
              />
            </span>
            <span className="cell user">Usuários</span>
            <span className="cell email">E-mail</span>
            <span className="cell shared">Compartilhado por</span>
            <span className="cell sharedAt">Compartilhado em</span>
            <span className="cell">Expira em</span>
          </div>

          {permissions.map((item) => (
            <div className="permissionsRow" key={item.id}>
              <span className="cell checkbox">
                <CheckBox
                  isActive={selectedPermissions.includes(item.id)}
                  event={() => toggleSelectOne(item.id)}
                  label=""
                />
              </span>
              <span className="cell user">
                {formatName(item.grantedTo?.name)}
              </span>
              <span className="cell email">{item.grantedTo?.email}</span>
              <span className="cell shared">
                {formatName(item.grantedBy?.name)}
              </span>
              <span className="cell sharedAt">
                {new Date(item.grantedAt).toLocaleDateString("pt-BR")}
              </span>
              <span className="cell">
                {item.expireAt
                  ? new Date(item.expireAt).toLocaleDateString("pt-BR")
                  : "—"}
              </span>
            </div>
          ))}
        </div>
        <div className="revokeActions">
          <SimpleButton
            text="Revogar acesso"
            classButton="buttonRed"
            toValidate={true}
            isValidForm={selectedPermissions.length > 0}
            clickEvent={handleRevoke}
          >
            <DeleteIcon color="#FFFFFF" />
          </SimpleButton>
        </div>
      </div>
    );
  };

  const CustomMenuList = (props) => {
    return (
      <components.MenuList {...props}>
        <div className="reactSelect__header">
          <span>NOME</span>
          <span>EMAIL</span>
          <span>CARGO</span>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  const CustomOption = (props) => {
    const { data } = props;

    return (
      <components.Option {...props}>
        <div className="reactSelect__optionRow">
          <span>{data.displayName}</span>
          <span>{data.email}</span>
          <span>{data.jobTitle}</span>
        </div>
      </components.Option>
    );
  };

  const CustomIndicatorsContainer = (props) => (
    <>
      <span className="selectLeftIcon">
        <PersonIcon />
      </span>
      <components.IndicatorsContainer {...props} />
    </>
  );

  const renderShareContent = () => (
    <div className="ViewWrapper">
      <div className="creatableFieldset">
        <AsyncSelect
          isMulti
          className="reactSelect"
          classNamePrefix="reactSelect"
          value={emails}
          loadOptions={searchUsers}
          onChange={handleChangeEmails}
          noOptionsMessage={() => "Nenhum resultado encontrado"}
          placeholder="Adicione um nome ou email de quem deve receber o arquivo"
          components={{
            IndicatorsContainer: CustomIndicatorsContainer,
            LoadingIndicator: () => <AsyncLoading />,
            Option: CustomOption,
            MenuList: CustomMenuList,
          }}
          loadingMessage={() => "Carregando..."}
          openMenuOnClick={false}
          blurInputOnSelect={true}
        />
      </div>

      <div className="message">
        <Textarea
          handleChange={handleChangeMessage}
          placeholder="Adicione uma mensagem (opcional)"
          maxRows={16}
          value={message}
        />
      </div>

      <div className="actions">
        <SimpleButton
          text="Enviar"
          classButton="buttonGreen"
          clickEvent={handleShare}
          toValidate={true}
          isValidForm={emails.length > 0}
        >
          <SendIcon />
        </SimpleButton>
      </div>
    </div>
  );

  const isSingleFile = selectedFiles.length === 1;

  return (
    <ScriptBox
      open={open}
      title={getTitle()}
      clear={handleClose}
      size="large"
      contentBg="#FFFFFF"
    >
      {isSingleFile && (
        <div className="fileShareTabs">
          <span
            className={`fileShareTab ${activeTab === "compartilhar" ? "active" : ""}`}
            onClick={() => handleTabChange("compartilhar")}
          >
            Compartilhar
          </span>

          <span
            className={`fileShareTab ${activeTab === "gerenciar" ? "active" : ""}`}
            onClick={() => handleTabChange("gerenciar")}
          >
            Gerenciar
          </span>
        </div>
      )}

      {isSingleFile ? (
        <>
          {activeTab === "compartilhar" && renderShareContent()}
          {activeTab === "gerenciar" && renderPermissions()}
        </>
      ) : (
        renderShareContent()
      )}
    </ScriptBox>
  );
};

export default SharedBox;
